import { useEffect, useRef } from 'react';
import { hasAuthSession, loadSupabase } from '@/lib/supabase-lazy';
import { track } from '@/lib/analytics/client';

interface UsePageVisitLoggerProps {
  appId: number | string;
  countryCode?: string;
}

export function usePageVisitLogger({ appId, countryCode }: UsePageVisitLoggerProps) {
  const loggedRef = useRef<string | null>(null);

  // Round 2 P4: previously this effect re-fired 2-4 times per page view
  // because `useSearchParams()` returns a new object reference on every render
  // and was in the dependency array. Combined with React Strict Mode dev
  // double-fires, /api/log-visit was being POSTed multiple times per visit,
  // each one queueing for a Supabase connection during pool exhaustion.
  //
  // Fix: drop pathname/searchParams from deps (they don't affect what we log),
  // and claim the loggedRef SYNCHRONOUSLY before any async work so that any
  // duplicate effect run bails out instantly without touching the network.
  // Fire-and-forget analytics POST (not render data); needs client session + sessionStorage + document.referrer, and is synchronously deduped via loggedRef so it can't race or double-fire.
  // oxlint-disable-next-line react-doctor/no-fetch-in-effect
  useEffect(() => {
    if (!appId || !countryCode) return;

    let sessionId = sessionStorage.getItem('app_session_id');
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem('app_session_id', sessionId);
    }
    const uniqueKey = `${sessionId}-${appId}-${countryCode}`;
    if (loggedRef.current === uniqueKey) return;
    loggedRef.current = uniqueKey;

    track('app_viewed', { app_id: Number(appId), country: countryCode ?? null });

    // The route only records signed-in visits, so anonymous visitors never need the client.
    if (!hasAuthSession()) return;

    (async () => {
      try {
        const supabase = await loadSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        // The route only records signed-in visits; an anonymous POST was a 401 in every visitor's console.
        if (!session) return;
        await fetch('/api/log-visit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionId,
            user_id: session?.user?.id,
            app_store_id: Number(appId),
            country_code: countryCode,
            referrer: document.referrer || null,
          }),
        });
      } catch (err) {
        console.error('Failed to log page visit:', err);
      }
    })();
  }, [appId, countryCode]);
}
